import omit from 'lodash/omit';
import pick from 'lodash/pick';
import shallowEqual from 'react-redux/lib/utils/shallowEqual';
import React, { Component } from 'react';
import filterBasicPropsOnly, { filterSupportedEventsOnly } from 'utils/filterBasicPropsOnly';

export function transferProps(props, tagName, omitProps) {
    let propsToTransfer;
    if (typeof tagName === 'string') {
        propsToTransfer = filterBasicPropsOnly(props);
    } else if (tagName && tagName.propTypes) {
        propsToTransfer = {
            ...filterSupportedEventsOnly(props),
            ...pick(props, Object.keys(tagName.propTypes)),
        };
    } else {
        propsToTransfer = props;
    }
    return omit(propsToTransfer, omitProps);
}

export { shallowEqual };

/**
 * Base Component
 * ==============
 *
 * This provides some boilerplate for creating React components in the "Splash way", using an
 * external stylesheet with className variations and certain inline overrides based on the props
 * that were passed into the component instance.
 */
export default class BaseComponent extends Component {
    /**
     * A "pure" component performs shallow prop and state comparison in shouldComponentUpdate by
     * default. Otherwise, shouldComponentUpdate always returns true and optimizations for are
     * deferred to the Virtual DOM comparisons instead.
     * @type {boolean}
     */
    static pure = true;

    /**
     * Compare the next and previous props are equal to determine if an update is necessary
     *
     * @param  {Object} nextProps - next props for this component
     * @param  {Object} prevProps - previous props for this component
     * @return {boolean} whether a difference was detected in the props
     */
    arePropsEqual(nextProps, prevProps) {
        return shallowEqual(nextProps, prevProps);
    }

    /**
     * Compare the next and previous state are equal to determine if an update is necessary
     *
     * @param  {Object} nextState - next state for this component
     * @param  {Object} prevState - previous state for this component
     * @return {boolean} whether a difference was detected in the state
     */
    areStatesEqual(nextState, prevState) {
        return shallowEqual(nextState, prevState);
    }

    /**
     * Determines whether the component needs to update based on changes to the props or state
     *
     * This can be overridden for performance optimizations to short-circuit the React component
     * tree rendering when the changes can be safely ignored.
     *
     * In BaseComponent, this will return true if it is NOT a pure component or if there is a change
     * in props or state, as determined by `arePropsEqual` and `areStatesEqual`, respectively.
     *
     * @param  {Object} nextProps [description]
     * @param  {Object} nextState [description]
     * @return {boolean} whether to render this component
     */
    shouldComponentUpdate(nextProps, nextState) {
        return (
            this.constructor.pure === false ||
            !this.arePropsEqual(nextProps, this.props) ||
            !this.areStatesEqual(nextState, this.state)
        );
    }

    /**
     * Default tagName to use for this element
     */
    static tagName = 'div';

    /**
     * Props to skip during transferProps
     *
     * @return {array}
     */
    static omitTransferProps = [];

    /**
     * Returns a base className for the component class that can be used as a prefix for variations
     *
     * @return {string} base className
     */
    static get baseClassName() {
        return this.name;
    }

    /**
     * Filters the props that are transferred to the element on render
     *
     * By default, this will filter down to basic supported HTML-properties for HTML tags (as
     * determined by whether the tagName is a string) and allow anything otherwise.
     * Overload to customize the filtering behavior, such as removing additional props or allowing
     * certain props that aren't event handlers or HTML attributes.
     *
     * @param  {Object} props - props to filter, defaults to the component props
     * @param  {Object} tagName - tagName to render the component
     * @param  {array} omitTransferProps - props that should NOT be transferred
     * @return {Object} filtered props
     */
    transferProps(props, tagName, omitTransferProps) {
        return transferProps(props, tagName, omitTransferProps);
    }

    /**
     * Creates a list of classNames based on the props
     *
     * Overriding this is preferred to overriding className() directly.
     *
     * @param  {Object} props - component props
     * @param  {string} baseClassName - base className to use as the prefix to other classNames
     * @return {Array} list of classNames generated from the component props
     */
    propClassNames(props, baseClassName) {
        return [];
    }

    /**
     * Creates styles based on the props
     *
     * Overriding this is preferred to overriding style() directly.
     *
     * @param  {Object} props - component props
     * @return {Object} inline styles generated from the component props
     */
    propStyles(props) {
        return {};
    }

    /**
     * Generates a tagName based on the props
     *
     * @param  {Object} props - component props
     * @return {string} default tagName to render
     */
    propTagName(props) {
        return props.tagName || this.constructor.tagName;
    }

    /**
     * Generates the component's children based on the props
     *
     * @param  {Object} props - component props
     * @param  {string} baseClassName - base className to use as the prefix to other classNames
     * @return {ReactElement} markup
     */
    propChildren(props, baseClassName) {
        return props.children;
    }

    /**
     * Returns the tagName used to render the component
     *
     * @return {string} component tagName
     */
    get tagName() {
        return this.propTagName(this.props);
    }

    /**
     * Returns the name of a ref for the root DOM element
     *
     * @return {string} root element ref
     */
    get ref() {
        return this.constructor.ref;
    }

    /**
     * Returns an array of props NOT to transfer in transferProps
     *
     * @return {array} props to omit
     */
    get omitTransferProps() {
        return this.constructor.omitTransferProps;
    }

    /**
     * Returns a base className to use as a prefix for variations
     *
     * If you want to override the base className, you probably want to do it on the static
     * baseClassName instead of here!
     *
     * @return {string} base className
     */
    get baseClassName() {
        return this.constructor.baseClassName;
    }

    /**
     * Builds a list of classNames for the component
     *
     * @return {string} className prop value
     */
    get className() {
        const { props, baseClassName } = this;
        const classNames = [baseClassName].concat(this.propClassNames(props, baseClassName));
        props.className && classNames.push(props.className);
        return classNames.join(' ');
    }

    /**
     * Builds inline styles for the component
     *
     * @return {Object} inline styles for the component
     */
    get style() {
        const { props } = this;
        return {
            ...this.propStyles(props),
            ...props.style,
        };
    }

    /**
     * Returns the children of this tag
     */
    get children() {
        return this.propChildren(this.props, this.baseClassName);
    }

    /**
     * Renders the component
     *
     * This passes along any transferrable props as well as the className and inline styles.
     *
     * @return {ReactElement} rendered component
     */
    render() {
        return (
            <this.tagName
                {...this.transferProps(this.props, this.tagName, this.omitTransferProps)}
                ref={this.ref}
                className={this.className}
                style={this.style}
                // eslint-disable-next-line react/no-children-prop
                children={this.children}
            />
        );
    }
}
